<template>
    <div class="ds-carousel">
        <Flicking
            ref="flic"
            :options
            @changed="onChanged"
        >
            <slot />
        </Flicking>
        <div v-if="slides.length > 1" class="controls">
            <button
                class="btn-previous"
                aria-label="previous"
                @click="$refs.flic.stopAnimation(); $refs.flic.prev()"
            >
                <div name="btn-left">
                    <DSIcon
                        code="chevron-left"
                        size="xxxl"
                        icon-style="line"
                    />
                </div>
            </button>
            <button
                class="btn-next"
                aria-label="next"
                @click="$refs.flic.stopAnimation(); $refs.flic.next()"
            >
                <div name="btn-right">
                    <DSIcon
                        code="chevron-right"
                        size="xxxl"
                        icon-style="line"
                    />
                </div>
            </button>
            <button
                v-if="showPauseButton && $bp.bps.isDesktop"
                class="btn-pause"
                aria-label="pause"
                @click="togglePause();"
            >
                <slot name="btn-pause">
                    <DSIcon
                        :code="paused ? 'chevron-right' : 'pause'"
                        icon-style="line"
                    />
                </slot>
            </button>
        </div>
        <div
            v-if="slides.length > 1"
            class="progressbar"
            aria-label="Slider navigation"
        >
            <button
                v-for="(slide, index) in slides"
                :key="index"
                class="progress-item"
                @click="changeTo(index)"
            >
                <div
                    class="bar"
                    :class="{
                        'current': index === activePanel,
                        'done': index < activePanel,
                    }"
                ></div>
                <p>{{ slide.title }}</p>
            </button>
        </div>
    </div>
</template>
<script setup>
import { ref, onMounted, onUnmounted, useNuxtApp } from '#imports';

const { $bp } = useNuxtApp();

const props = defineProps({
    slides: {
        type: Array,
        required: false,
        default: () => [],
    },
    autoplayTimeoutMs: {
        type: Number,
        required: false,
        default: 0,
    },
    showPauseButton: {
        type: Boolean,
        required: false,
        default: false,
    },
    showArrows: {
        type: Boolean,
        required: false,
        default: true,
    },
    options: {
        type: Object,
        required: false,
        default: () => ({
            align: 'center',
            circular: true,
            bound: true,
        }),
    },
});

const activePanel = ref(0);
const paused = ref(false);
const progressBarWidth = ref(0);
const flic = ref(null);
const timeoutTime = props.autoplayTimeoutMs/100;
const timeout = ref(null);
const resizeTimeout = ref(null);

const startInterval = () => {
    if (timeout.value) {
        clearTimeout(timeout.value);
    }

    timeout.value = setTimeout(() => {
        if (!paused.value) progressBarWidth.value = progressBarWidth.value + 1;

        const transitionOffset = 2;

        if (progressBarWidth.value < 100 + transitionOffset) {
            startInterval();
        } else if (flic.value !== null) {
            flic.value.next();
        }
    }, timeoutTime);
};

onMounted(() => {
    if (props.autoplayTimeoutMs && props.slides.length > 1) {
        startInterval();
    }

    window.addEventListener('resize', resizePanels);
});

onUnmounted(() => {
    window.removeEventListener('resize', resizePanels);
});

function onChanged(e) {
    progressBarWidth.value = 0;
    activePanel.value = e.index;
    clearTimeout(timeout.value);
    if (!paused.value) startInterval();
}

function changeTo(index) {
    clearTimeout(timeout.value);
    progressBarWidth.value = 0;
    flic.value.moveTo(index);
    activePanel.value = index;
}

function togglePause() {
    paused.value = !paused.value;
    if (!paused.value) startInterval();
}

function resizePanels() {
    if (resizeTimeout.value) {
        window.clearTimeout(resizeTimeout.value);
    }

    window.setTimeout(() => {
        flic.value.stopAnimation();
        flic.value.resize();
        changeTo(0);
    }, 50);
}
</script>
<style>
@import "@egjs/vue3-flicking/dist/flicking-inline.css";
@import "@egjs/flicking-plugins/dist/flicking-plugins.css";
.ds-carousel .panel {
  width: 100%;
}
.ds-carousel .flicking-camera {
  display: flex;
  flex-flow: row nowrap;
}
.ds-carousel .flicking-camera > * {
  display: flex;
  min-width: 100%;
  align-self: stretch;
  align-items: flex-start;
}
.ds-carousel .progressbar {
  display: flex;
  flex-flow: row wrap;
  justify-content: space-evenly;
  align-items: flex-start;
  margin: 2rem 0;
}
.ds-carousel .progressbar .progress-item {
  width: calc(20% - 2rem);
  position: relative;
  text-align: left;
}
.ds-carousel .progressbar .progress-item p {
  margin-top: 0.5rem;
}
.ds-carousel .progressbar .progress-item .bar {
  position: relative;
  height: 0.25rem;
  transform-origin: left center;
  background: var(--color-base-grey-200);
  overflow: hidden;
  transition: width 0.2s ease;
}
.ds-carousel .progressbar .progress-item .bar.current::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.25s linear;
  display: block;
  transform: scaleX(v-bind(progressBarWidth/100));
  transform-origin: left center;
  background-color: var(--color-primary-500);
}
.ds-carousel .progressbar .progress-item .done {
  background-color: var(--color-primary-500);
}
@media (max-width: 1199px) {
  .ds-carousel .progressbar {
    display: none;
  }
}
.ds-carousel .btn-pause {
  position: absolute;
  top: 1rem;
  right: 1rem;
  box-shadow: 0.125rem 0.125rem rgba(0, 0, 0, 0.2);
  z-index: 1;
  -webkit-tap-highlight-color: transparent;
}
.ds-carousel .btn-previous {
  position: absolute;
  top: 50%;
  left: 1rem;
  z-index: 1;
  box-shadow: 0.125rem 0.125rem rgba(0, 0, 0, 0.2);
  -webkit-tap-highlight-color: transparent;
}
.ds-carousel .btn-previous * {
  display: grid;
}
.ds-carousel .btn-next {
  position: absolute;
  top: 50%;
  right: 1rem;
  z-index: 1;
  box-shadow: 0.125rem 0.125rem rgba(0, 0, 0, 0.2);
  -webkit-tap-highlight-color: transparent;
}
.ds-carousel .btn-next * {
  display: grid;
}
</style>
